import { useState, useEffect, useRef } from "react"
import { lumi } from "../lib/lumi"
import { useAuth } from "../hooks/useAuth"
import {Bell, X, CheckCircle, XCircle, Clock} from 'lucide-react'

interface NotificationBellProps {
  onNavigateToReport: (weekStart: string, weekEnd: string) => void
}

export function NotificationBell({ onNavigateToReport }: NotificationBellProps) {
  const { user } = useAuth()
  const [notifications, setNotifications] = useState<any[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (user) {
      // 최초 진입 시에는 안 읽은 개수만 경량 조회
      loadUnreadCount()
      // B 최적화: 30초→60초로 완화, 전송량 최소화를 위해 unread 카운트만 주기 조회
      const interval = setInterval(loadUnreadCount, 60000)
      return () => clearInterval(interval)
    }
  }, [user])

  // 드롭다운을 열 때만 본문 포함 전체 목록을 조회 (B 최적화)
  useEffect(() => {
    if (showDropdown && user) {
      loadNotifications()
    }
  }, [showDropdown])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // B 최적화: 평상시에는 안 읽은 개수 계산에 필요한 최소 필드만 projection으로 조회
  const loadUnreadCount = async () => {
    if (!user) return

    try {
      const result = await lumi.entities.notifications.list({
        filter: {
          userId: user.userId,
          isDeleted: false,
          isRead: false
        },
        projection: { _id: 1 },
        limit: 100
      })
      setUnreadCount(result.list.length)
    } catch (error) {
      console.error("Failed to load unread count:", error)
    }
  }

  const loadNotifications = async () => {
    if (!user) return

    try {
      const result = await lumi.entities.notifications.list({
        filter: {
          userId: user.userId,
          isDeleted: false
        },
        sort: { createdAt: -1 },
        limit: 50
      })

      setNotifications(result.list)
      const unread = result.list.filter((n: any) => !n.isRead).length
      setUnreadCount(unread)
    } catch (error) {
      console.error("Failed to load notifications:", error)
    }
  }

  const markAsRead = async (notificationId: string) => {
    try {
      await lumi.entities.notifications.update(notificationId, {
        isRead: true
      })
      await loadNotifications()
    } catch (error) {
      console.error("Failed to mark notification as read:", error)
    }
  }

  const deleteNotification = async (notificationId: string, event: React.MouseEvent) => {
    event.stopPropagation()
    
    try {
      await lumi.entities.notifications.delete(notificationId)
      await loadNotifications()
    } catch (error) {
      console.error("Failed to delete notification:", error)
    }
  }

  const deleteAllNotifications = async () => {
    if (!user || notifications.length === 0) return
    
    if (!confirm('모든 알림을 삭제하시겠습니까?')) return
    
    try {
      const notificationIds = notifications.map(n => n._id)
      await Promise.all(
        notificationIds.map(id => lumi.entities.notifications.delete(id))
      )
      await loadNotifications()
    } catch (error) {
      console.error("Failed to delete all notifications:", error)
    }
  }

  const handleNotificationClick = async (notification: any) => {
    // 읽음 표시
    if (!notification.isRead) {
      await markAsRead(notification._id)
    }

    // 해당 주간보고로 이동
    setShowDropdown(false)
    onNavigateToReport(notification.weekStart, notification.weekEnd)
  }

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "submitted":
        return <Clock className="w-5 h-5 text-blue-600" />
      case "approved":
        return <CheckCircle className="w-5 h-5 text-green-600" />
      case "rejected":
        return <XCircle className="w-5 h-5 text-red-600" />
      default:
        return <Bell className="w-5 h-5 text-gray-600" />
    }
  }

  const getNotificationColor = (type: string) => {
    switch (type) {
      case "submitted":
        return "bg-blue-50 border-blue-200"
      case "approved":
        return "bg-green-50 border-green-200"
      case "rejected":
        return "bg-red-50 border-red-200"
      default:
        return "bg-gray-50 border-gray-200"
    }
  }

  const formatRelativeTime = (dateStr: string) => {
    const now = new Date()
    const date = new Date(dateStr)
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return "방금 전"
    if (diffMins < 60) return `${diffMins}분 전`
    if (diffHours < 24) return `${diffHours}시간 전`
    if (diffDays < 7) return `${diffDays}일 전`
    
    return date.toLocaleDateString("ko-KR", {
      month: "short",
      day: "numeric"
    })
  }

  const formatWeekRange = (weekStart: string) => {
    const date = new Date(weekStart)
    const month = date.getMonth() + 1
    
    // 해당 월의 몇 번째 주인지 계산
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1)
    const weekNumber = Math.ceil((date.getDate() + firstDay.getDay()) / 7)
    
    return `${month}월 ${weekNumber}주차`
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 hover:bg-gray-100 rounded-lg transition-colors"
      >
        <Bell className="w-6 h-6 text-gray-700" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 max-h-[500px] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-amber-50">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900">알림</h3>
                {unreadCount > 0 && (
                  <p className="text-sm text-gray-600">읽지 않은 알림 {unreadCount}개</p>
                )}
              </div>
              {notifications.length > 0 && (
                <button
                  onClick={deleteAllNotifications}
                  className="text-sm text-orange-600 hover:text-orange-700 font-medium transition-colors"
                >
                  전체 삭제
                </button>
              )}
            </div>
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto flex-1">
            {notifications.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p>알림이 없습니다</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notification) => (
                  <div
                    key={notification._id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors relative ${
                      !notification.isRead ? "bg-blue-50/50" : ""
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1">
                        {getNotificationIcon(notification.type)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 mb-1">
                          {notification.message}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span>{formatWeekRange(notification.weekStart)}</span>
                          <span>•</span>
                          <span>{formatRelativeTime(notification.createdAt)}</span>
                        </div>
                        {!notification.isRead && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                            새 알림
                          </span>
                        )}
                      </div>

                      <button
                        onClick={(e) => deleteNotification(notification._id, e)}
                        className="flex-shrink-0 p-1 hover:bg-gray-200 rounded transition-colors"
                      >
                        <X className="w-4 h-4 text-gray-400" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
